import { history } from 'prosemirror-history'

import { actSlugTrackerPlugin } from './act-slug-tracker'
import { baseKeymapPlugin } from './base-keymap'
import { blockInfoFactory } from './block-info/blockInfo'
import { markChangesPlugin } from './change-tracking/markChanges'
import { characterAutocompletePlugin } from './character-autocomplete.js'
import { characterContdsPlugin } from './character/'
import { collabUpdatePlugin } from './collab-update'
import { commentsPlugin } from './comments/index'
import { configDataPlugin } from './configData'
import { decorateSelectionPlugin } from './decorateSelection'
import { docInfoFactory } from './doc-info'
import { editorKeymapPlugin } from './editor-keymap'
import { elementPickerKeymapPlugin } from './element-picker-keymap'
import { elementNumbersPlugin } from './elementNumbers'
import { emptyBlockCyclePlugin } from './empty-block-cycle'
import { ensureNewlinePlugin } from './ensure-newline'
import { createFocusLockPlugin } from './focus-lock'
import { idMakerPlugin } from './id-maker.js'
import { indentationStylesFactory } from './indentation-styles'
import { inkInputRulesPlugin } from './ink-input-rules'
import { inkKeymapPlugin } from './ink-keymap'
import { inlineManualPageBreaksPlugin } from './inlineManualPageBreaks'
import { inlinePageBreaksPlugin } from './inlinePageBreaks'
import { inputRulesPlugin } from './input-rules.js'
import { createNextBlockPlugin } from './next-block'
import { createPaginationPlugin } from './pagination'
import { recordDomPositions } from './recordDomPositions'
import { recordLastPositionAdapter } from './recordLastPosition'
import { relaxedSideWidgetHandler as relaxedSideWidgetHandlerPlugin } from './relaxed-side-widget-handler'
import { remoteCollaboratorsPlugin } from './remoteCollaborators'
import { removeFormatInfoPlugin } from './removeFormatInfoPlugin'
import { removeStructuralPaginationPlugin } from './removeStructuralPagination'
import { createRevisionsConfig, revisionsPlugin } from './revisions'
import { scrollManagerPlugin } from './scroll-manager'
import * as SplitPoints from './split-points'
import { syntaxHighlightingFactory } from './syntax-highlighting'
import { titlePageViewPlugin } from './titlePageView'
import { EditorContext, EnabledCheck, PluginConfig } from './types'

const editableCheck: EnabledCheck = ({ script }) => script.isEditable

const combineChecks =
  (...checkFns: EnabledCheck[]): EnabledCheck =>
  (ctx: EditorContext) =>
    checkFns.every((fn) => fn(ctx))

const isInk: EnabledCheck = ({ script }) => script.isInk
const notInk: EnabledCheck = ({ script }) => !script.isInk

const isPageless: EnabledCheck = ({ mst }) => !!mst.user.prefs?.pageless

export const configData: PluginConfig = {
  key: 'configData',
  plugin: configDataPlugin,
}

export const docInfo: PluginConfig = {
  key: 'docInfo',
  plugin: docInfoFactory,
}

export const blockInfo: PluginConfig = {
  key: 'blockInfo',
  plugin: blockInfoFactory,
}

export const actSlugTracker: PluginConfig = {
  key: 'actSlugTracker',
  plugin: actSlugTrackerPlugin,
}

export const collabUpdate: PluginConfig = {
  key: 'collabUpdate',
  plugin: collabUpdatePlugin,
}

export const characterAutocomplete: PluginConfig = {
  key: 'characterAutocomplete',
  plugin: characterAutocompletePlugin,
  checkEnabled: editableCheck,
}

export const characterContds: PluginConfig = {
  key: 'characterContd',
  plugin: characterContdsPlugin,
}

export const inputRules: PluginConfig = {
  key: 'inputRules',
  plugin: inputRulesPlugin,
  checkEnabled: notInk,
}

export const nextBlock: PluginConfig = {
  key: 'nextBlock',
  plugin: () => createNextBlockPlugin(),
}

export const emptyBlockCycle: PluginConfig = {
  key: 'emptyBlockCycle',
  plugin: () => emptyBlockCyclePlugin,
  checkEnabled: ({ script }) => script.type === 'screenplay',
}

export const editorKeymap: PluginConfig = {
  key: 'editorKeymap',
  plugin: editorKeymapPlugin,
  checkEnabled: notInk,
}

export const elementPickerKeymap: PluginConfig = {
  key: 'elementPickerKeymap',
  plugin: elementPickerKeymapPlugin,
  checkEnabled: notInk,
}

export const baseKeymap: PluginConfig = {
  key: 'baseKeymap',
  plugin: baseKeymapPlugin,
}

export const focusLock: PluginConfig = {
  key: 'focusLock',
  plugin: () => createFocusLockPlugin(),
}

export const ensureNewline: PluginConfig = {
  key: 'ensureNewline',
  plugin: ({ mst }) => {
    const debug = mst.view.isDebugEnabled('show-newline')
    return ensureNewlinePlugin({ debug })
  },
}

export const remoteCollaborators: PluginConfig = {
  key: 'remoteCollaborators',
  plugin: remoteCollaboratorsPlugin,
}

export const idMaker: PluginConfig = {
  key: 'idMaker',
  plugin: idMakerPlugin,
  checkEnabled: editableCheck,
}

export const historyPlugin: PluginConfig = {
  key: 'history',
  plugin: () => history(),
  checkEnabled: editableCheck,
}

export const recordDomPositionsPlugin: PluginConfig = {
  key: 'recordDomPositions',
  plugin: recordDomPositions,
}

export const recordLastPosition: PluginConfig = {
  key: 'recordLastPosition',
  plugin: recordLastPositionAdapter,
}

export const comments: PluginConfig = {
  key: 'comments',
  plugin: commentsPlugin,
  checkEnabled: ({ script }) => script.canAddComments,
}

export const elementNumbers: PluginConfig = {
  key: 'elementNumbers',
  plugin: elementNumbersPlugin,
  checkEnabled: editableCheck,
}

export const inkKeymap: PluginConfig = {
  key: 'inkKeymap',
  plugin: inkKeymapPlugin,
  checkEnabled: combineChecks(editableCheck, isInk),
}

export const inkInputRules: PluginConfig = {
  key: 'inkInputRules',
  plugin: inkInputRulesPlugin,
  checkEnabled: combineChecks(editableCheck, isInk),
}

export const indentationStyles: PluginConfig = {
  key: 'detectIndentation',
  plugin: indentationStylesFactory,
  checkEnabled: isInk,
}

export const decorateSelection: PluginConfig = {
  key: 'decorateSelection',
  plugin: decorateSelectionPlugin,
}

export const syntaxHighlighting: PluginConfig = {
  key: 'syntaxHighlighting',
  plugin: syntaxHighlightingFactory,
  checkEnabled: isInk,
}

export const removeStructuralPagination: PluginConfig = {
  key: 'removeStructuralPagination',
  plugin: removeStructuralPaginationPlugin,
  checkEnabled: editableCheck,
}

export const removeFormatInfo: PluginConfig = {
  key: 'removeFormatInfo',
  plugin: removeFormatInfoPlugin,
  checkEnabled: editableCheck,
}

export const inlineManualPageBreaks: PluginConfig = {
  key: 'inlineManualPageBreaks',
  plugin: inlineManualPageBreaksPlugin,
  checkEnabled: isPageless,
}

export const inlinePageBreaks: PluginConfig = {
  key: 'inlinePageBreaks',
  plugin: ({ script, user }) =>
    inlinePageBreaksPlugin({
      get name() {
        return script.name
      },
      scriptType: script.type,
      readRate: script.readRate,
      scriptPrintPrefs: user.scriptPrintPrefs,
    }),
  checkEnabled: ({ mst }) => {
    if (mst.user.prefs?.pageless) return false
    return !mst.view.useNewPaginator
  },
}

export const splitPoints: PluginConfig = {
  key: 'splitPoints',
  plugin: SplitPoints.pluginFactory,
}

export const relaxedSideWidgetHandler: PluginConfig = {
  key: 'relaxedSideWidgetHandler',
  plugin: () => relaxedSideWidgetHandlerPlugin,
}

export const markChanges: PluginConfig = {
  key: 'markChanges',
  plugin: markChangesPlugin,
  checkEnabled: ({ mst }) => !!mst.currentScript?.trackingChanges,
}

export const revisions: PluginConfig = {
  key: 'revisions',
  plugin({ capabilities }) {
    return revisionsPlugin(createRevisionsConfig(capabilities.revisions))
  },
  // without a loader the plugin would never leave its loading state
  checkEnabled: ({ capabilities }) => !!capabilities.revisions,
}

export const titlePageView: PluginConfig = {
  key: 'titlePageView',
  plugin: () => titlePageViewPlugin,
}

export const scrollManager: PluginConfig = {
  key: 'scrollManager',
  plugin: scrollManagerPlugin,
}

export const paginationPlugin: PluginConfig = {
  key: 'pagination',
  plugin: ({ script, user, mst }) =>
    createPaginationPlugin(
      {
        get name() {
          return script.name
        },
        scriptType: script.type,
        readRate: script.readRate,
        scriptPrintPrefs: user.scriptPrintPrefs,
      },
      () => mst.view.isDebugEnabled('split-points'),
    ),
  checkEnabled: ({ mst }) => {
    if (mst.user.prefs?.pageless) return false
    return mst.view.useNewPaginator
  },
}
